import { supabase } from './supabaseClient';

export interface AppSettings {
  lateToleranceMin: number;
  pdvRadiusM: number;
  clockSkewMin: number;
  docWarnDays: number;
  /** % del turno por debajo del cual la jornada cuenta como MEDIO día (regla del
   *  medio día, 0016). El servidor usa el mismo valor de `app_settings`. */
  halfDayThresholdPct: number;
}

/** Defaults del contrato: se usan si la fila no existe o la BD no responde. */
export const SETTINGS_DEFAULTS: AppSettings = {
  lateToleranceMin: 15,
  pdvRadiusM: 300,
  clockSkewMin: 10,
  docWarnDays: 30,
  halfDayThresholdPct: 75,
};

/**
 * Alias del nombre que introdujo la Task 6. NO borrar: `app/tests/lib/settings.test.ts`
 * (Task 6) importa `DEFAULT_SETTINGS` y `tsconfig.json` incluye `tests`, así que sin
 * este export `npx tsc --noEmit` falla con TS2305 y `npm run test` no resuelve el import.
 */
export const DEFAULT_SETTINGS: AppSettings = SETTINGS_DEFAULTS;

export const SETTINGS_KEYS: Record<keyof AppSettings, string> = {
  lateToleranceMin: 'late_tolerance_min',
  pdvRadiusM: 'pdv_radius_m',
  clockSkewMin: 'clock_skew_min',
  docWarnDays: 'doc_warn_days',
  halfDayThresholdPct: 'half_day_threshold_pct',
};

let cache: AppSettings | null = null;

/** Invalida la caché tras guardar en Ajustes. */
export function clearSettingsCache(): void {
  cache = null;
}

export async function loadSettings(): Promise<AppSettings> {
  if (cache) return cache;
  const { data, error } = await supabase.from('app_settings').select('key, value');
  if (error) {
    console.warn('No se pudieron cargar los parámetros; se usan los valores por defecto.', error.message);
    return { ...SETTINGS_DEFAULTS };
  }
  const map = new Map(((data ?? []) as { key: string; value: unknown }[]).map((r) => [r.key, r.value]));
  const read = (k: keyof AppSettings): number => {
    const raw = map.get(SETTINGS_KEYS[k]);
    if (raw === null || raw === undefined) return SETTINGS_DEFAULTS[k];
    const n = Number(raw);
    return Number.isFinite(n) ? n : SETTINGS_DEFAULTS[k];
  };
  cache = {
    lateToleranceMin: read('lateToleranceMin'),
    pdvRadiusM: read('pdvRadiusM'),
    clockSkewMin: read('clockSkewMin'),
    docWarnDays: read('docWarnDays'),
    halfDayThresholdPct: read('halfDayThresholdPct'),
  };
  return cache;
}

/** Guarda los parámetros (upsert por `key`) e invalida la caché. Solo admin por RLS. */
export async function saveSettings(values: AppSettings): Promise<void> {
  const rows = (Object.keys(SETTINGS_KEYS) as (keyof AppSettings)[]).map((k) => ({
    key: SETTINGS_KEYS[k],
    value: values[k],
  }));
  const { error } = await supabase.from('app_settings').upsert(rows, { onConflict: 'key' });
  if (error) throw error;
  clearSettingsCache();
}
